import api from '../utils/api';

class CourtService {
  // ========== ADMIN ENDPOINTS ==========
  // Get all courts for admin management
  async getAllCourts() {
    try {
      const response = await api.get('/admin/courts');
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get all public courts
  async getPublicCourts() {
    try {
      const response = await api.get('/public/courts');
      return response.data; // Direct array, not ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get all cameras with their courts (admin)
  async getAllCameras() {
    try {
      const response = await api.get('/admin/courts/cameras');
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Assign court ownership (admin)
  async assignCourtOwnership(courtId, username) {
    try {
      await api.post(`/admin/courts/${courtId}/owner/${username}`);
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Add a co-owner to a court (admin)
  async addCourtCoOwner(courtId, username) {
    try {
      await api.post(`/admin/courts/${courtId}/co-owners/${username}`);
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Add a new court for a given owner (admin)
  async addCourt(courtDto, ownerUsername) {
    try {
      const response = await api.post('/admin/courts', courtDto, {
        params: { ownerUsername }
      });
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // ========== REGULAR COURT ENDPOINTS ==========
  // Get all courts for court owners
  async getCourts() {
    try {
      const response = await api.get('/courts/mine');
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Add a new court (site owner)
  async addCourtForOwner(courtDto) {
    try {
      const response = await api.post('/courts', courtDto);
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Update a court (site owner)
  async updateCourtForOwner(courtId, courtDto) {
    try {
      const response = await api.put(`/courts/${courtId}`, courtDto);
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get court by DB ID (site owner)
  async getCourtByDbId(id) {
    try {
      const response = await api.get(`/courts/db/${id}`);
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get court by courtId UUID (site owner)
  async getCourtByCourtIdForOwner(courtId) {
    try {
      const response = await api.get(`/courts/by-court-id/${courtId}`);
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Delete court by DB ID (site owner)
  async deleteCourtByDbId(id) {
    try {
      await api.delete(`/courts/db/${id}`);
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Delete court by courtId UUID (site owner)
  async deleteCourtByCourtIdForOwner(courtId) {
    try {
      await api.delete(`/courts/by-court-id/${courtId}`);
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Start court stream (site owner)
  async startCourtStream(streamRequest) {
    try {
      const response = await api.post('/courts/start-stream', streamRequest);
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Stop court stream (site owner)
  async stopCourtStream(stopRequest) {
    try {
      await api.post('/courts/stop-stream', stopRequest);
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Get court owner usernames
  async getCourtOwnerUsernames(courtId) {
    try {
      const response = await api.get(`/courts/${courtId}/owners`);
      return response.data.data; // ApiEnvelope wrapper
    } catch (error) {
      throw new Error(this._extractErrorMessage(error));
    }
  }

  // Validate court data before submission
  validateCourtData(courtData) {
    const errors = [];
    
    // Validate court name
    if (!courtData.courtName || courtData.courtName.trim() === '') {
      errors.push('Court name is required');
    }
    
    // Validate HLS URL (optional but if provided, must be valid)
    if (courtData.hlsUrl && courtData.hlsUrl.trim() !== '') {
      try {
        new URL(courtData.hlsUrl);
      } catch {
        errors.push('HLS URL must be a valid URL');
      }
    }
    
    // Validate cameras
    if (!courtData.cameras || courtData.cameras.length === 0) {
      errors.push('At least one camera is required');
    } else {
      courtData.cameras.forEach((camera, index) => {
        if (!camera.cameraName || camera.cameraName.trim() === '') {
          errors.push(`Camera ${index + 1} name is required`);
        }
        if (!camera.rtspUrl || camera.rtspUrl.trim() === '') {
          errors.push(`Camera ${index + 1} URL is required`);
        } else {
          try {
            new URL(camera.rtspUrl);
          } catch {
            errors.push(`Camera ${index + 1} URL must be a valid URL`);
          }
        }
      });
    }
    
    // Validate sessions (optional but if provided, validate structure)
    if (courtData.sessions && courtData.sessions.length > 0) {
      courtData.sessions.forEach((session, index) => {
        if (!session.sessionName || session.sessionName.trim() === '') {
          errors.push(`Session ${index + 1} name is required`);
        }
        if (!session.dayOfWeek) {
          errors.push(`Session ${index + 1} day of week is required`);
        }
        if (!session.startTime) {
          errors.push(`Session ${index + 1} start time is required`);
        }
        if (!session.endTime) {
          errors.push(`Session ${index + 1} end time is required`);
        }
      });
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  // Helper method to extract error message
  _extractErrorMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred';
  }
}

const courtService = new CourtService();
export default courtService;